import { DogSchool } from "@/data/dogSchools";
import { 
  getCoordinatesByPostalCode, 
  getCoordinatesByCity
} from "./coordinate-lookup";
import { getSupabase } from "@/integrations/supabase/client";
import { mapSupabaseRowToDogSchool } from "@/utils/supabaseMappers";
import { Json } from "@/integrations/supabase/types";

// Default coordinates for countries
const getDefaultCoordinates = (country: string): { lat: number, lng: number } => {
  switch(country.toLowerCase()) {
    case 'germany':
      return { lat: 51.1657, lng: 10.4515 }; // Germany center
    case 'austria':
      return { lat: 47.5162, lng: 14.5501 }; // Austria center
    case 'switzerland':
      return { lat: 46.8182, lng: 8.2275 }; // Switzerland center
    default:
      return { lat: 51.1657, lng: 10.4515 }; // Default to Germany
  }
};

interface SchoolWithDistance {
  google_id: string;
  name: string | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  latitude: number;
  longitude: number;
  distance_km: number;
  exact_match: boolean;
  site: string | null;
  phone: string | null;
  about: Json | null;
  working_hours: string | null;
  photo: string | null;
  reviews: string | null;
  reviewsrating: string | null;
  reviewscount: string | null;
}

interface FindSchoolsParams {
  search_lat: number;
  search_lng: number;
  radius_km: number;
  search_postal_code: string | null;
}

/**
 * Fetch dog schools by postal code and radius from Supabase
 */
export async function fetchDogSchoolsByPostalCode(
  postalCode: string,
  city?: string,
  radius: number = 50,
  country: string = 'DE'
): Promise<DogSchool[]> {
  try {
    const startTime = performance.now();
    console.log('\n🔍 =============== DOG SCHOOL SEARCH START ===============');
    console.log('📋 Search Parameters:');
    console.table({
      postalCode,
      city: city || 'Not provided',
      radius: `${radius}km`,
      country
    });

    // Get search coordinates
    let searchCoordinates;
    if (postalCode) {
      searchCoordinates = await getCoordinatesByPostalCode(postalCode);
      console.log('🔍 Coordinates from postal code:', searchCoordinates || 'Not found');
    }

    if (!searchCoordinates && city) {
      searchCoordinates = await getCoordinatesByCity(city);
      console.log('🔍 Coordinates from city:', searchCoordinates || 'Not found');
    }

    if (!searchCoordinates) {
      const countryName = country === 'DE' ? 'Germany' : 
                         country === 'AT' ? 'Austria' : 
                         country === 'CH' ? 'Switzerland' : 'Germany';
      searchCoordinates = getDefaultCoordinates(countryName);
      console.log('⚠️ Using default coordinates for', countryName, ':', searchCoordinates);
    }

    // Fetch schools using the new SQL function
    console.log('\n📊 Step 1: Fetching Schools from Database');
    const { data: schools, error: dbError } = await getSupabase()
      .rpc('find_schools_in_radius', {
        search_lat: searchCoordinates.lat,
        search_lng: searchCoordinates.lng,
        radius_km: radius,
        search_postal_code: postalCode
      });

    if (dbError) {
      console.error('❌ Database query failed:', dbError.message);
      return [];
    }

    if (!schools?.length) {
      console.log('⚠️ No schools found within the specified radius');
      return [];
    }

    console.log(`✅ Retrieved ${schools.length} schools within ${radius}km radius`);

    // Convert to DogSchool objects
    const dogSchools = schools.map(school => ({
      ...mapSupabaseRowToDogSchool({
        google_id: school.google_id,
        name: school.name,
        street: school.street,
        postal_code: school.postal_code,
        city: school.city,
        latitude: school.latitude,
        longitude: school.longitude,
        site: school.site,
        phone: school.phone,
        about: school.about,
        working_hours: school.working_hours,
        photo: school.photo,
        reviews: school.reviews,
        reviewsrating: school.reviewsrating,
        reviewscount: school.reviewscount
      }),
      distance: school.distance_km,
      exactMatch: school.exact_match
    }));

    // Performance measurement and results
    const duration = (performance.now() - startTime) / 1000;
    const exactMatches = dogSchools.filter(s => s.exactMatch).length;

    console.log('\n📊 =============== SEARCH RESULTS ===============');
    console.log(`🎯 Exact postal matches: ${exactMatches}`);
    console.log(`🌍 Total schools in radius: ${dogSchools.length}`);
    console.log(`📍 Search coordinates: [${searchCoordinates.lat}, ${searchCoordinates.lng}]`);
    console.log(`⏱️ Search duration: ${duration.toFixed(2)} seconds`);

    // Detailed results
    console.log('\n📋 Schools found (first 10):');
    dogSchools.slice(0, 10).forEach((school, index) => {
      console.log(`${index + 1}. ${school.name}`);
      console.log(`   📍 ${school.postalCode} ${school.city}`);
      console.log(`   📏 Distance: ${school.distance.toFixed(2)}km`);
      if (school.exactMatch) console.log(`   ✨ Exact postal code match`);
    });

    if (dogSchools.length > 10) {
      console.log(`... and ${dogSchools.length - 10} more schools`);
    }

    console.log('\n🔍 =============== SEARCH COMPLETE ===============\n');
    return dogSchools;

  } catch (error) {
    console.error('\n❌ Error in fetchDogSchoolsByPostalCode:', error);
    return [];
  }
}
